import React from 'react';
import { Pencil, ReceiptText, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { EmptyState, StatusPill, type StatusTone } from '../../../components/app';
import { cn } from '../../../lib/utils';
import { AccrualsTableProps } from '../types';
import { Accrual } from '../../../api/cashflow';
import { formatDay } from '../format';

/**
 * Премиум покупателя: подписка активна, если флаг стоит и дата не прошла.
 * Начисление её не выдаёт — метка нужна, чтобы заметить, кому забыли выдать.
 */
const premiumLabel = (user: Accrual['user']): { text: string; tone: StatusTone | null } => {
  if (!user) return { text: '—', tone: null };
  const until = user.subscribedEnd ? new Date(user.subscribedEnd) : null;
  if (!user.isSubscribed || !until || until <= new Date()) {
    return { text: 'нет премиума', tone: 'warn' };
  }
  return { text: `до ${until.toLocaleDateString('ru-RU')}`, tone: 'pos' };
};

export function AccrualsTable({ accruals, isLoading, onEdit, onDelete, formatMoney }: AccrualsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Дата продажи</TableHead>
          <TableHead>Провайдер</TableHead>
          <TableHead className="text-right">Сумма</TableHead>
          <TableHead className="text-right">В сумах</TableHead>
          <TableHead>Покупатель</TableHead>
          <TableHead>Премиум</TableHead>
          <TableHead>Комментарий</TableHead>
          <TableHead className="w-[1%]">
            <span className="sr-only">Действия</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array.from({ length: 5 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: 8 }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full max-w-24" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : accruals.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={8}>
              <EmptyState
                icon={<ReceiptText />}
                title="Начислений за период нет"
                description="Начисления заводятся у провайдеров без платежей в базе — например, Apple."
              />
            </TableCell>
          </TableRow>
        ) : (
          accruals.map((accrual) => {
            const premium = premiumLabel(accrual.user);
            return (
              <TableRow key={accrual.id}>
                <TableCell className="whitespace-nowrap tabular-nums">{formatDay(accrual.date)}</TableCell>
                <TableCell className="font-semibold">{accrual.provider?.name || '—'}</TableCell>
                <TableCell
                  className={cn('text-right font-semibold tabular-nums whitespace-nowrap', accrual.amount < 0 && 'text-neg')}
                >
                  {formatMoney(accrual.amount, accrual.currency)}
                </TableCell>
                <TableCell className="text-right text-text-2 tabular-nums whitespace-nowrap">
                  {formatMoney(accrual.amount * accrual.rate, 'UZS')}
                </TableCell>
                <TableCell>{accrual.user ? accrual.user.username : '—'}</TableCell>
                <TableCell>
                  {premium.tone ? (
                    <StatusPill size="sm" tone={premium.tone}>
                      {premium.text}
                    </StatusPill>
                  ) : (
                    <span className="text-text-3">—</span>
                  )}
                </TableCell>
                <TableCell className="max-w-xs truncate text-text-2">{accrual.comment || '—'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon-sm" onClick={() => onEdit(accrual)} aria-label="Изменить начисление">
                      <Pencil className="text-text-2" />
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => onDelete(accrual.id)} aria-label="Удалить начисление">
                      <Trash2 className="text-neg" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
